"use client"

import { motion } from "motion/react"
import { Search, PenTool, Database, Rocket, ShieldCheck } from "lucide-react"

export default function MethodologyStack() {
  const steps = [
    {
      id: "01",
      title: "Discovery & Roadmapping",
      description: "Deep dive into your product requirements, user workflows, and business goals before writing code.",
      icon: Search,
      color: "#6366f1" // Indigo
    },
    {
      id: "02",
      title: "System Architecture",
      description: "Designing the technical blueprint, API specs, database schemas, and AI pipeline infrastructure.",
      icon: PenTool,
      color: "#8b5cf6" // Violet
    },
    {
      id: "03",
      title: "Agile Development",
      description: "Continuous 48–72 hour build cycles with weekly working demos, clean code, and zero fluff.",
      icon: Database,
      color: "#ec4899" // Pink
    },
    {
      id: "04",
      title: "Testing & Quality Assurance",
      description: "Comprehensive automated testing, security validation, and performance optimization.",
      icon: ShieldCheck,
      color: "#10b981" // Emerald
    },
    {
      id: "05",
      title: "Launch & Support",
      description: "Smooth production deployment, live monitoring, complete handover, and ongoing feature updates.",
      icon: Rocket,
      color: "#f59e0b" // Amber
    }
  ]

  return (
    <div id="methodology" className="relative bg-background py-24 transition-colors duration-300">
      <div className="container mx-auto px-4 mb-24 text-center">
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          className="text-primary/80 uppercase tracking-widest text-sm font-medium mb-4 block"
        >
          Delivery Process
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-5xl font-bold text-foreground mb-6"
        >
          How We Build &amp; Ship
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-muted-foreground max-w-2xl mx-auto"
        >
          A predictable, transparent engineering process designed to take your idea from concept to live production.
        </motion.p>
      </div>

      <div className="container mx-auto px-4">
        <div className="relative max-w-5xl mx-auto">
          {/* Connecting Line (Vertical) */}
          <div className="absolute left-[28px] md:left-1/2 top-0 bottom-0 w-0.5 bg-linear-to-b from-primary/50 via-primary/20 to-transparent -translate-x-1/2" />

          <div className="space-y-12 md:space-y-24 pb-24">
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`flex flex-col md:flex-row gap-8 items-center relative ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
              >
                {/* Number/Icon Node */}
                <div className="absolute left-0 md:left-1/2 -translate-x-1/2 flex items-center justify-center z-10">
                  <div
                    className="w-14 h-14 rounded-full border-4 border-background flex items-center justify-center shadow-[0_0_20px_rgba(0,0,0,0.1)] dark:shadow-[0_0_20px_rgba(0,0,0,0.5)] transition-colors duration-300"
                    style={{ backgroundColor: step.color }}
                  >
                    <step.icon className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Content Card */}
                <div className={`w-full md:w-[calc(50%-40px)] pl-16 md:pl-0 ${index % 2 === 0 ? 'md:text-right md:pr-10' : 'md:text-left md:pl-10'}`}>
                  <div className="bg-card border border-border/20 p-8 rounded-3xl hover:border-primary/30 transition-colors group shadow-md dark:shadow-none">
                    <span
                      className="text-xs font-bold tracking-widest uppercase mb-2 block"
                      style={{ color: step.color }}
                    >
                      Step {step.id}
                    </span>
                    <h3 className="text-2xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Spacer for the other side */}
                <div className="hidden md:block w-[calc(50%-40px)]" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
